import {useSessionPropsExport} from "@AppBuilderLib/entities/export/model/useSessionPropsExport";
import {useSessionPropsOutput} from "@AppBuilderLib/entities/output/model/useSessionPropsOutput";
import {useSessionPropsParameter} from "@AppBuilderLib/entities/parameter/model/useSessionPropsParameter";
import {useViewportAnchors} from "@AppBuilderLib/entities/viewport/model/useViewportAnchors";
import {
	additionalWithMobileFallbackInjections,
	defaultsWithoutHiddenOriginals,
	planMobileFallbacks,
} from "@AppBuilderLib/features/appbuilder/lib/applyMobileFallbacks";
import {mergeAllStandardContainers} from "@AppBuilderLib/features/appbuilder/lib/mergeStandardContainerContent";
import {useAppBuilderMobileLayoutTheme} from "@AppBuilderLib/features/appbuilder/model/useAppBuilderMobileLayoutTheme";
import {
	IAppBuilderTemplatePageContainerHints,
	IAppBuilderTemplatePageProps,
} from "@AppBuilderLib/pages/config/appbuildertemplates";
import {Logger} from "@AppBuilderLib/shared/lib/logger";
import AppBuilderContainerComponent from "@AppBuilderLib/widgets/appbuilder/ui/AppBuilderContainerComponent";
import AppBuilderFallbackContainerComponent from "@AppBuilderLib/widgets/appbuilder/ui/AppBuilderFallbackContainerComponent";
import {useMantineTheme} from "@mantine/core";
import {useMediaQuery} from "@mantine/hooks";
import {useContext, useEffect, useMemo} from "react";
import {
	AppBuilderContainerNameType,
	IAppBuilder,
	IAppBuilderSettingsResolved,
	IAppBuilderSettingsSession,
	IAppBuilderStandardContainer,
	isStandardContainer,
} from "../config/appbuilder";
import {ComponentContext} from "../config/ComponentContext";
import {AppBuilderStandardContainerNameType} from "../config/shapediverStoreStandardContainers";
import {useShapeDiverStoreStandardContainers} from "./useShapeDiverStoreStandardContainers";

interface Props {
	namespace: string;
	appBuilderData?: IAppBuilder;
	sessionSettings?: IAppBuilderSettingsSession;
	settings?: IAppBuilderSettingsResolved;
	hasAppBuilderOutput: boolean;
}

/**
 * Create rendering hints for the container.
 * @param container
 * @returns
 */
const createContainerHints = (
	container: IAppBuilderStandardContainer,
): IAppBuilderTemplatePageContainerHints | undefined => {
	// if the bottom container contains tabs, prefer vertical layout
	if (
		container.name === AppBuilderContainerNameType.Bottom &&
		container.tabs &&
		container.tabs.length > 0
	) {
		return {
			preferVertical: true,
		};
	}
};

/**
 * Hook to manage standard containers for the app builder.
 * This hook provides the necessary props and state management for the standard containers.
 * It also handles the viewport anchors.
 *
 * Once the mergedContainers are available, it creates the necessary UI elements for each container.
 *
 * @param props
 * @returns
 */
export function useAppBuilderStandardContainers(props: Props) {
	const {
		namespace,
		appBuilderData,
		sessionSettings,
		settings,
		hasAppBuilderOutput,
	} = props;

	const componentContext = useContext(ComponentContext);
	const ContainerComponent =
		componentContext.containerComponent || AppBuilderContainerComponent;
	const FallbackContainerComponent =
		componentContext.fallbackContainerComponent ||
		AppBuilderFallbackContainerComponent;

	// get props for fallback parameters
	const parameterProps = useSessionPropsParameter(namespace);
	const exportProps = useSessionPropsExport(namespace);
	const outputProps = useSessionPropsOutput(
		namespace,
		(output) => !!output.chunks,
	);

	const {
		mergedContainers,
		defaultContainers,
		additionalContainerContent,
		activeTabIndices,
		containerOpen,
		resetDefaultContainers,
		setDefaultContainers,
	} = useShapeDiverStoreStandardContainers((state) => ({
		mergedContainers: state.mergedContainers,
		defaultContainers: state.defaultContainers,
		additionalContainerContent: state.additionalContainerContent,
		activeTabIndices: state.activeTabIndices,
		containerOpen: state.containerOpen,
		resetDefaultContainers: state.resetDefaultContainers,
		setDefaultContainers: state.setDefaultContainers,
	}));

	const {mobileBreakpoint, mobileFallbacks} =
		useAppBuilderMobileLayoutTheme();
	const theme = useMantineTheme();
	const aboveMobileBreakpoint = useMediaQuery(
		`(min-width: ${theme.breakpoints[mobileBreakpoint]})`,
		true,
	);

	const layoutContainers = useMemo(() => {
		if (aboveMobileBreakpoint !== false) {
			return mergedContainers;
		}
		const plan = planMobileFallbacks(defaultContainers, mobileFallbacks, {
			onWarn: (message) => Logger.warn(message),
		});
		if (plan.hideOriginal.length === 0 && plan.injections.length === 0) {
			return mergedContainers;
		}
		const stripped = defaultsWithoutHiddenOriginals(
			defaultContainers,
			plan.hideOriginal,
		);
		const additional = additionalWithMobileFallbackInjections(
			additionalContainerContent,
			plan,
			(from, to) => {
				const source = defaultContainers[from];
				if (!source || !ContainerComponent) {
					return undefined;
				}
				return (
					<ContainerComponent
						namespace={namespace}
						{...source}
						name={to}
					/>
				);
			},
		);
		return mergeAllStandardContainers(
			stripped,
			additional,
			activeTabIndices,
		);
	}, [
		aboveMobileBreakpoint,
		mergedContainers,
		defaultContainers,
		additionalContainerContent,
		activeTabIndices,
		mobileFallbacks,
		namespace,
		ContainerComponent,
	]);

	// viewport anchors
	const anchors = useViewportAnchors({
		namespace,
		containers: appBuilderData?.containers,
		actionSlots: appBuilderData?.actionSlots,
	});

	// should fallback containers be shown?
	const showFallbackContainers =
		settings?.settings?.disableFallbackUi !== true;

	// Update store with default containers whenever appBuilderData changes
	// This only stores the container definition within the store
	useEffect(() => {
		// Reset containers first
		resetDefaultContainers();

		if (appBuilderData?.containers) {
			const standardContainers = appBuilderData.containers
				.filter((container) => isStandardContainer(container))
				.reduce(
					(acc, container) => ({
						...acc,
						[container.name]: container,
					}),
					{} as Record<
						AppBuilderStandardContainerNameType,
						IAppBuilderStandardContainer | undefined
					>,
				);
			setDefaultContainers(standardContainers);
		}
	}, [
		appBuilderData?.containers,
		setDefaultContainers,
		resetDefaultContainers,
	]);

	// Fallback container logic
	// We memoize it and add it in the next useMemo
	const fallbackContainer = useMemo(() => {
		const hasFallbackData =
			parameterProps.length > 0 ||
			exportProps.length > 0 ||
			outputProps.length > 0;

		if (
			!hasAppBuilderOutput &&
			hasFallbackData &&
			showFallbackContainers &&
			FallbackContainerComponent
		) {
			return {
				node: (
					<FallbackContainerComponent
						parameters={parameterProps}
						exports={exportProps}
						outputs={outputProps}
						namespace={namespace}
						settings={sessionSettings}
					/>
				),
			};
		}
		return undefined;
	}, [
		hasAppBuilderOutput,
		showFallbackContainers,
		parameterProps,
		exportProps,
		outputProps,
		namespace,
		sessionSettings,
		FallbackContainerComponent,
	]);

	//create UI elements for containers
	const containers: IAppBuilderTemplatePageProps = useMemo(() => {
		const result: IAppBuilderTemplatePageProps = {
			top: undefined,
			bottom: undefined,
			left: undefined,
			right: undefined,
		};

		const mergedContainerArray = Object.values(layoutContainers).filter(
			(container): container is IAppBuilderStandardContainer =>
				!!container && containerOpen[container.name],
		);

		if (mergedContainerArray.length === 0) {
			if (fallbackContainer) {
				result.right = fallbackContainer;
			}
		} else {
			mergedContainerArray.forEach((container) => {
				// these checks should never fail
				// we just have to do them here for type safety
				if (!container || !isStandardContainer(container)) return;
				result[container.name] = {
					node: ContainerComponent ? (
						<ContainerComponent
							namespace={namespace}
							{...container}
						/>
					) : undefined,
					hints: createContainerHints(container),
				};
			});
		}

		return result;
	}, [
		layoutContainers,
		containerOpen,
		namespace,
		fallbackContainer,
		ContainerComponent,
	]);

	return {containers, anchors};
}
